import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import { API } from '../../config';
import { useAuth } from '../../contexts/AuthContext';
import { getPushPermissionState } from '../../services/pushService';
import { setPriorityPopupBlocking, usePriorityPopupBlocking } from './priorityPopupStore';
import AgeConfirmModal from './AgeConfirmModal';
import AlertsPopup from './AlertsPopup';

const SESSION_LATCH = 'alerts_popup_session_shown';
const EXCLUDED = /^\/(login|register|auth|admin|terms|privacy|cookie|legal|reset-password|verify-email)/;

const HOME = '/dashboard';
const ANALYSIS = /^\/match\//;

const isFuture = (iso) => !!iso && new Date(iso).getTime() > Date.now();

const decideAlerts = (state, pushState) => {
  try { if (sessionStorage.getItem(SESSION_LATCH) === '1') return null; } catch { /* noop */ }
  const snooze = state.alerts_popup || {};
  if (isFuture(snooze.snooze_until)) return null;
  const needsEmail = !state.marketing_consent_verified;
  const needsPush = pushState === 'prompt' && !isFuture(snooze.push_snooze_until);
  const needsMeta = !!state.needs_meta;
  if (!needsEmail && !needsPush && !needsMeta) return null;
  return { needsEmail, needsPush, needsMeta, reconfirm: !!state.marketing_consent, team: state.favorite_team || null };
};

// Shows at most one of: 18+ confirmation (priority 1), alerts popup (priority 2).
// The alerts popup waits until the user has opened an analysis and is back on Home.
export const PriorityPopups = () => {
  const { user, loading } = useAuth();
  const { pathname } = useLocation();
  const [step, setStep] = useState(null);
  const stepRef = useRef(null);
  stepRef.current = step;
  const [alerts, setAlerts] = useState(null);
  const [consent, setConsent] = useState(null);
  const [analysisOpened, setAnalysisOpened] = useState(false);
  const evaluatedRef = useRef(false);
  const loadedUidRef = useRef(null);
  const excluded = EXCLUDED.test(pathname);
  const uid = user?.user_id;

  const evaluateAlerts = async (state) => {
    const pushState = await getPushPermissionState().catch(() => 'unsupported');
    const next = decideAlerts(state, pushState);
    if (!next) return;
    try { sessionStorage.setItem(SESSION_LATCH, '1'); } catch { /* noop */ }
    setPriorityPopupBlocking(true);
    setAlerts(next);
    setStep('alerts');
  };

  useEffect(() => {
    if (!uid || !consent || analysisOpened || !ANALYSIS.test(pathname)) return;
    setAnalysisOpened(true);
    axios.post(`${API}/auth/analysis-opened`).catch(() => {});
  }, [uid, consent, analysisOpened, pathname]);

  useEffect(() => {
    if (!uid || !consent || step || !analysisOpened || pathname !== HOME || evaluatedRef.current) return;
    evaluatedRef.current = true;
    evaluateAlerts(consent);
  }, [uid, consent, step, analysisOpened, pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (loading && !uid) return undefined;
    if (stepRef.current && uid && !excluded) return undefined;
    if (!uid || excluded) {
      setStep(null);
      setPriorityPopupBlocking(false);
      return undefined;
    }
    if (loadedUidRef.current === uid) return undefined;
    loadedUidRef.current = uid;
    evaluatedRef.current = false;
    setConsent(null);
    let cancelled = false;
    let done = false;
    setPriorityPopupBlocking(true);
    axios.get(`${API}/auth/consent-state`).then((r) => {
      if (cancelled) return;
      done = true;
      const data = r.data || {};
      setAnalysisOpened(!!data.alerts_popup?.analysis_opened_at);
      if (data.needs_age_confirmation) { setStep('age'); setAlerts(data); return; }
      setConsent(data);
      setPriorityPopupBlocking(false);
    }).catch(() => { loadedUidRef.current = null; if (!cancelled) setPriorityPopupBlocking(false); });
    return () => { cancelled = true; if (!done) loadedUidRef.current = null; };
  }, [uid, excluded, loading]); // eslint-disable-line react-hooks/exhaustive-deps

  if (excluded || !step) return null;
  if (step === 'age') {
    return <AgeConfirmModal onConfirmed={() => { setStep(null); setPriorityPopupBlocking(false); setConsent(alerts || {}); }} />;
  }
  return (
    <AlertsPopup
      needsEmail={alerts.needsEmail}
      needsMeta={alerts.needsMeta}
      needsPush={alerts.needsPush}
      reconfirm={alerts.reconfirm}
      initialTeam={alerts.team}
      onDone={() => { setStep(null); setPriorityPopupBlocking(false); }}
    />
  );
};

// Renders children only when no priority popup is pending.
export const AfterPriorityPopups = ({ children }) => (usePriorityPopupBlocking() ? null : <>{children}</>);

export default PriorityPopups;
